import React, { useState, useRef, useEffect } from 'react';
import styles from './CampoUpload.module.css';
import Icon from '@/components/Icon';
import { mdiUpload, mdiFileUploadOutline, mdiClose } from '@mdi/js';
import FormLabel from './FormLabel';
import InputError from './InputError';

type CampoUploadProps = {
    instanceId?: string;
    label?: string;
    value?: File[];
    onChange: (files: File[]) => void;
    error?: string;
    maxFiles?: number;
    maxSize?: number;  // em bytes
    accept?: string;
    placeholder?: string;
    tooltipText?: string;
    tooltipPosition?: 'left' | 'right';
    disabled?: boolean;
    size?: 'sm' | 'lg';
    /** Esconde a lista interna de arquivos, para quem quer desenhar a própria. */
    hideFileList?: boolean;
};

function CampoUpload({
    instanceId,
    label,
    value = [],
    onChange,
    error,
    maxFiles = 10,
    maxSize = 25 * 1024 * 1024,  // 25MB por padrão
    accept,
    placeholder = 'Arraste arquivos aqui ou clique para selecionar',
    tooltipText,
    tooltipPosition,
    disabled,
    size = 'lg',
    hideFileList,
}: CampoUploadProps) {
    const [isDragging, setIsDragging] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | undefined>(error);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setErrorMsg(error);
    }, [error]);

    useEffect(() => {
        if (!value) {
            fileInputRef.current!.value = '';
        }
    }, [value]);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const validateFiles = (files: File[]): File[] => {
        // Verificar quantidade de arquivos
        if (value.length + files.length > maxFiles) {
            setErrorMsg(`Máximo de ${maxFiles} arquivos permitidos.`);
            return files.slice(0, maxFiles - value.length);
        }

        // Verificar tipo e tamanho de cada arquivo
        const validFiles = files.filter(file => {
            if (accept) {
                const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
                const acceptedExtensions = accept.split(',').map(ext => ext.trim().toLowerCase());
                const isValidExtension = acceptedExtensions.some(ext => {
                    if (ext.startsWith('.')) {
                        return fileExtension === ext;
                    }
                    return file.type.match(ext.replace(/\*/g, '.*'));
                });

                if (!isValidExtension) {
                    setErrorMsg(`Tipo de arquivo não permitido: ${file.name}`);
                    return false;
                }
            }

            if (file.size > maxSize) {
                const sizeMB = Math.round(maxSize / (1024 * 1024));
                setErrorMsg(`Arquivo muito grande. Máximo permitido: ${sizeMB}MB`);
                return false;
            }

            return true;
        });

        if (validFiles.length === files.length) {
            setErrorMsg(undefined);
        }

        return validFiles;
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const newFiles = Array.from(e.dataTransfer.files);
            const validFiles = validateFiles(newFiles);

            if (validFiles.length > 0) {
                onChange([...value, ...validFiles]);
            }
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);
            const validFiles = validateFiles(newFiles);

            if (validFiles.length > 0) {
                onChange([...value, ...validFiles]);
            }
        }

        // Resetar o campo para permitir carregar o mesmo arquivo novamente
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = (index: number) => {
        const newFiles = [...value];
        newFiles.splice(index, 1);
        onChange(newFiles);
        setErrorMsg(undefined);
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    return (
        <div className={styles.container}>
            {label && (
                <FormLabel
                    htmlFor={instanceId}
                    tooltipText={tooltipText}
                    tooltipPosition={tooltipPosition}
                >
                    {label}
                </FormLabel>
            )}

            <div
                className={`${styles.dropzone} ${isDragging ? styles.dragging : ''} ${errorMsg ? styles.error : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    id={instanceId}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept={accept}
                    multiple={maxFiles > 1}
                    disabled={disabled}
                />

                <div className={[styles.placeholderContent, size === 'sm' ? styles.small : styles.large, disabled ? styles.disabled : ''].join(' ')}>
                    <div><Icon path={mdiUpload} size={1.5} color="var(--tc-color-gray-500)" /></div>
                    <p>{placeholder}</p>
                    <small>Tipos permitidos: {accept || 'Todos'}</small>
                    <small>Tamanho máximo: {formatFileSize(maxSize)}</small>
                </div>
            </div>

            {errorMsg && <InputError error={errorMsg} />}

            {value.length > 0 && !hideFileList && (
                <div className={styles.fileList}>
                    {value.map((file, index) => (
                        <div key={`${file.name}-${index}`} className={styles.fileItem}>
                            <div className={styles.fileIcon}>
                                <Icon path={mdiFileUploadOutline} size={0.9} />
                            </div>
                            <div className={styles.fileInfo}>
                                <div className={styles.fileName}>{file.name}</div>
                                <div className={styles.fileSize}>{formatFileSize(file.size)}</div>
                            </div>
                            <button
                                type="button"
                                className={styles.removeButton}
                                onClick={() => handleRemoveFile(index)}
                                aria-label="Remover arquivo"
                            >
                                <Icon path={mdiClose} size={0.7} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default CampoUpload;
